import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Select } from '@artosera/ui';
import { useTranslation } from 'react-i18next';
import { boiteApi } from '../../api/boite-reception.js';
import { appelDeRequete, cleMetier } from '../../api/cles.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { NotFoundPage } from '../introuvable/NotFoundPage.js';
import { Field } from '../Field.js';
import styles from './Boite.module.css';
export function DelaisPage() {
  const { galerie } = useShellContext();
  if (!galerieAModule(galerie, 'boite-reception') || !galerieALaction(galerie, 'inbox:settings'))
    return <NotFoundPage />;
  return <Delais />;
}
function Delais() {
  const { galerieSlug } = useShellContext();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const [jours, setJours] = useState(30);
  const delais = useQuery({
    queryKey: cleMetier(galerieSlug, 'boite-reception', 'delais', jours),
    queryFn: (c) => boiteApi.delais(galerieSlug, jours, appelDeRequete(c)),
  });
  const nombre = (v: number | null) =>
    v === null ? '—' : new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(v);
  return (
    <section className={styles.page}>
      <h1>{t('regie:boiteReception.delais')}</h1>
      <p>{t('regie:boiteReception.heuresCalendaires')}</p>
      <Field htmlFor="delais-periode" label={t('regie:boiteReception.periode')}>
        <Select
          id="delais-periode"
          value={String(jours)}
          onValueChange={(v) => setJours(Number(v))}
          options={[7, 30, 90].map((j) => ({
            value: String(j),
            label: t('regie:boiteReception.jours', { count: j }),
          }))}
        />
      </Field>
      {delais.isLoading ? <p role="status">{t('commun:chargement')}</p> : null}
      {delais.isError ? <p role="alert">{t('commun:erreurReseau')}</p> : null}
      {delais.data ? (
        <>
          {(['parCanal', 'parCategorie'] as const).map((groupe) => (
            <div key={groupe} className={styles.tableau}>
              <table className={styles.table}>
                <caption>{t(`regie:boiteReception.${groupe}`)}</caption>
                <thead>
                  <tr>
                    {['groupe', 'recues', 'mediane', 'p90', 'dansObjectif'].map((c) => (
                      <th key={c} scope="col">
                        {t(`regie:boiteReception.${c}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(delais.data[groupe]).map(([cle, s]) => (
                    <tr key={cle}>
                      <th scope="row">
                        {t(
                          `regie:boiteReception.${groupe === 'parCanal' ? 'canaux' : 'categories'}.${cle}`,
                        )}
                      </th>
                      <td>{s.conversationsRecues}</td>
                      <td>{nombre(s.medianeHeures)}</td>
                      <td>{nombre(s.p90Heures)}</td>
                      <td>
                        {s.partDansObjectif === null
                          ? '—'
                          : `${nombre(s.partDansObjectif * 100)} %`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          <h2>{t('regie:boiteReception.enRetard')}</h2>
          <ul>
            {delais.data.ouvertesSansReponse
              .filter((c) => c.retardHeures > 0)
              .map((c) => (
                <li key={c.id}>
                  <Link to={`/${galerieSlug}/boite-reception/${c.id}`}>
                    {c.sujet || t('regie:boiteReception.sansSujet')}
                  </Link>{' '}
                  · {t('regie:boiteReception.retardHeures', { heures: nombre(c.retardHeures) })}
                </li>
              ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}
